import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { getTransportIcon } from "./experienceUtils";
import { isSameDay } from "date-fns";
import { calculateStartTime, getAllImages } from "@/lib/experiences";
import {
  WaytoraExperienceInitial,
  ItineraryItem,
  BookingStatus,
} from "@/types";
import MapView from "@tripbuilder/cat-experiences/viator/modal/Map";
import { getMainImage, getCoordinatesFromPlaceId } from "@/lib/experiences";
import { toTitleCase, getLocationTypeIcon } from "@/lib/general";
import placeholderImage from "@/assets/imgloading.png";
import waytoraLogo from "@/assets/logos/internal/waytora_blue_text_logo.svg";
import useTripStore from "@/stores/tripBuilder/useTrip";

// Import the new FullScreenGallery component
import ImageGalleryModal from "@tripbuilder/image/FullScreenGallery";
import { motion } from "framer-motion";

interface SectionContainerProps {
  children: React.ReactNode;
  className?: string;
}

const SectionContainer: React.FC<SectionContainerProps> = ({
  children,
  className,
}) => <div className={`w-full `}>{children}</div>;

interface ExperienceAttributeProps {
  icon: string;
  name: string;
  value: string | number;
  className?: string;
}

const ExperienceAttribute: React.FC<ExperienceAttributeProps> = ({
  icon,
  name,
  value,
  className,
}) => {
  let displayValue = value;
  if (name === "Time of Day to Visit" && typeof value === "string") {
    displayValue = value.split(/ [oO][rR] /)[0] || value;
  }
  return (
    <div
      className={`flex flex-col items-start justify-start ring-1 ring-inset ring-neutral-200 shadow rounded-lg p-2 w-full ${className}`}
    >
      <div className="flex items-center space-x-2.5 mb-2">
        <i className={`${icon} text-neutral-700 text-base`}></i>
        <h1 className="font-semibold text-neutral-900 text-xs">{name}</h1>
      </div>
      <p className="text-neutral-700 font-light text-[13px] leading-relaxed">
        {name === "Price Level" ? (
          <>
            <span className="text-neutral-900">
              {"$".repeat(Number(value))}
            </span>
            <span className="text-neutral-400">
              {"$".repeat(5 - Number(value))}
            </span>
          </>
        ) : (
          <>
            {displayValue}
            {name === "Time to Spend" &&
              (value === 1 ? (
                <span className="text-neutral-400"> hour</span>
              ) : (
                <span className="text-neutral-400"> hours</span>
              ))}
            {name === "Family Friendliness" && (
              <span className="text-neutral-400"> out of 5</span>
            )}
          </>
        )}
      </p>
    </div>
  );
};

const typeToIconClass: Record<string, string> = {
  historical: "fa-regular fa-monument",
  religious: "fa-regular fa-church",
  restaurants: "fa-regular fa-fork-knife",
  shopping: "fa-regular fa-shopping-cart",
  clubs: "fa-regular fa-champagne-glasses",
  theatre: "fa-regular fa-theater-masks",
  museums: "fa-regular fa-building-columns",
  museum: "fa-regular fa-building-columns",
  "art gallery": "fa-regular fa-palette",
  "national park": "fa-regular fa-trees",
  viewpoint: "fa-regular fa-binoculars",
  parks: "fa-regular fa-tree",
  "street market": "fa-regular fa-store",
  "food market": "fa-regular fa-pot-food",
  restaurant: "fa-regular fa-fork-knife",
  bar: "fa-regular fa-glass-whiskey",
  stadium: "fa-regular fa-court-sport",
  "natural wonder": "fa-regular fa-sparkle",
  "hiking trail": "fa-regular fa-person-hiking",
  wildlife: "fa-regular fa-paw",
  beach: "fa-regular fa-umbrella-beach",
  waterfall: "fa-regular fa-water",
  "amusement park": "fa-regular fa-roller-coaster",
  lake: "fa-regular fa-water-ladder",
  diving: "fa-regular fa-mask-snorkel",
  residential: "fa-regular fa-home",
  surfing: "fa-regular fa-wave",
};

interface ImageWithFadeProps {
  url: string;
  alt: string;
  placeholderImg: string;
}

const ImageWithFade: React.FC<ImageWithFadeProps> = ({
  url,
  alt,
  placeholderImg,
}) => {
  const [currentSrc, setCurrentSrc] = useState(placeholderImg);
  const blurredImage = `https://waytora.com/cdn-cgi/image/width=300,height=300,quality=50/${url}`;
  const highQualityImage = `https://waytora.com/cdn-cgi/image/width=800,height=800,quality=100/${url}`;

  useEffect(() => {
    const blurImg = new Image();
    blurImg.src = blurredImage;
    blurImg.onload = () => {
      const highQualityImg = new Image();
      highQualityImg.src = highQualityImage;
      highQualityImg.onload = () => {
        setCurrentSrc(highQualityImage);
      };
    };

    return () => {
      blurImg.onload = null;
    };
  }, [url, blurredImage, highQualityImage]);

  return (
    <div className="relative h-64 w-64 flex-shrink-0">
      <img
        src={currentSrc}
        alt={alt}
        className="h-64 w-64 object-cover rounded-md transition-opacity duration-300 ease-in-out"
      />
    </div>
  );
};

interface CarouselProps {
  images: Array<{
    url: string;
    isMain: boolean;
    date?: string;
    artist?: string;
    license?: string;
  }>;
  openModal: () => void;
}

const Carousel: React.FC<CarouselProps> = ({ images, openModal }) => {
  return (
    <div className="relative w-full flex flex-col items-center justify-start">
      <div
        id="expPhotoCarousel"
        className="w-full h-64 flex items-center space-x-2 overflow-x-auto rounded-md whitespace-nowrap scroll-smooth no-scrollbar"
      >
        {images.map((image, index) => (
          <ImageWithFade
            key={`${image.url}-${index}-${image.date}`}
            url={image.url}
            alt={`Experience ${index + 1}`}
            placeholderImg={placeholderImage}
          />
        ))}
      </div>
      <button
        onClick={openModal}
        className="absolute bottom-2 right-2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 p-2 rounded-lg text-xs font-medium transition-all duration-200 shadow-md"
      >
        View All
      </button>
    </div>
  );
};

interface AddressComponentProps {
  address: string;
  isLoading: boolean;
  experienceName: string;
}

const AddressComponent: React.FC<AddressComponentProps> = ({
  address,
  isLoading,
  experienceName,
}) => {
  const [showFull, setShowFull] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const isLongAddress = address.length > 55;
  const truncatedAddress = isLongAddress
    ? `${address.slice(0, 55)}...`
    : address;

  const handleMouseEnter = useCallback(() => {
    if (isLongAddress && !isLoading) {
      timerRef.current = setTimeout(() => setShowFull(true), 500);
    }
  }, [isLongAddress, isLoading]);

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setShowFull(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <div
      className="flex flex-row items-center space-x-2 text-neutral-600 relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <i className="fa-light fa-map-marker-alt fa-md text-[15px]"></i>
      {isLoading ? (
        <div className="inline-flex items-center space-x-1 h-[20px]">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{
                repeat: Infinity,
                duration: 0.6,
                delay: i * 0.1,
                ease: "easeInOut",
              }}
              className="w-1.5 h-1.5 bg-indigo-800 rounded-full inline-block"
            />
          ))}
        </div>
      ) : (
        <p
          className={`text-[13px] font-light ${
            showFull ? "bg-white rounded-md shadow-md p-2 z-10" : ""
          }`}
        >
          {showFull ? address : truncatedAddress}
        </p>
      )}
    </div>
  );
};

const addFullStop = (tip: string) => {
  const trimmedSentence = tip.trim();

  if (trimmedSentence.length === 0) {
    return tip;
  }

  const lastChar = trimmedSentence[trimmedSentence.length - 1]!;

  if (![".", "!", "?"].includes(lastChar)) {
    return trimmedSentence + ".";
  }

  return trimmedSentence;
};

interface MoreInfoModalProps {
  updateTripData: (tripData: any) => void;
  selectedExperience: WaytoraExperienceInitial | null;
  placeId: string;
  handleClose: (modalRef: any) => void;
  isAddDestinationModalOpen: boolean;
  shouldClose: boolean;
  isAdmin: boolean;
}

const MoreInfoModal: React.FC<MoreInfoModalProps> = ({
  updateTripData,
  selectedExperience,
  placeId,
  handleClose,
  isAddDestinationModalOpen,
  shouldClose,
  isAdmin,
}) => {
  const { tripData, selectedDay, selectedLocation, addExperienceToItinerary } =
    useTripStore();

  const [isAdding, setIsAdding] = useState(false);
  const [height, setHeight] = useState(window.innerHeight - 500);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldClose) {
      handleClose(modalRef);
    }
  }, [shouldClose, handleClose]);

  useEffect(() => {
    if (isAddDestinationModalOpen) {
      handleClose(modalRef);
    }
  }, [isAddDestinationModalOpen, handleClose]);

  const isLiked = useMemo(
    () =>
      tripData?.liked_experiences.includes(
        selectedExperience?.waytora_place_id || ""
      ),
    [tripData?.liked_experiences, selectedExperience?.waytora_place_id]
  );

  const { data: placeLocationDetails, isLoading: isLoadingPlaceDetails } =
    useQuery<{
      address: string;
      latitude: number;
      longitude: number;
    } | null>({
      queryKey: ["coordinates", placeId],
      queryFn: async () => {
        if (
          !selectedExperience?.waytora_place_id ||
          !selectedLocation ||
          !selectedExperience?.name
        ) {
          return null;
        }
        // Check if the experience already has address and coordinates
        if (
          selectedExperience.address &&
          selectedExperience.latitude &&
          selectedExperience.longitude
        ) {
          return {
            address: selectedExperience.address,
            latitude: selectedExperience.latitude,
            longitude: selectedExperience.longitude,
          };
        }
        // If not, fetch the data
        return getCoordinatesFromPlaceId(
          selectedExperience.waytora_place_id,
          selectedLocation,
          selectedExperience.name
        );
      },
      staleTime: Infinity,
      enabled: !!selectedExperience,
    });

  const handleLeftClick = () => {
    const carousel = document.getElementById(
      "expPhotoCarousel"
    ) as HTMLDivElement;
    carousel.scrollBy({ left: -300, behavior: "smooth" });
    setCurrentImageIndex(currentImageIndex - 1);
  };

  const handleRightClick = () => {
    const carousel = document.getElementById(
      "expPhotoCarousel"
    ) as HTMLDivElement;
    carousel.scrollBy({ left: 300, behavior: "smooth" });
    setCurrentImageIndex(currentImageIndex + 1);
  };

  const handleAddToDay = async () => {
    if (!selectedExperience || !selectedLocation || !selectedDay || !isAdmin) {
      console.error("Missing data for adding experience from modal.");
      return;
    }

    setIsAdding(true);
    try {
      await addExperienceToItinerary(
        selectedExperience,
        selectedDay,
        selectedLocation.id,
        placeLocationDetails || undefined
      );
    } catch (error) {
      console.error(
        "Error calling addExperienceToItinerary from modal:",
        error
      );
    }
    setIsAdding(false);
  };

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const openGallery = () => setIsGalleryOpen(true);

  return (
    <motion.div
      className="absolute inset-0 bg-white/20 backdrop-blur-sm z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="w-full h-full p-3 flex flex-col gap-1">
        <div className="h-full bg-white/90 backdrop-blur-xl rounded-lg px-4 pb-2 flex flex-col border border-neutral-200">
          <div className="flex flex-col items-center justify-start relative">
            <div className="w-full overflow-y-scroll no-scrollbar h-[calc(100vh-3rem)] rounded-lg mt-2 pb-[5rem]">
              <div className="flex flex-row justify-between items-start pt-3">
                <div className="flex-1 max-w-[calc(100%-5rem)] sticky top-0 z-10 pb-1">
                  <div className="flex flex-col items-start justify-between">
                    <div className="pb-2">
                      <img
                        src={waytoraLogo}
                        alt="Waytora Logo"
                        className="w-20 object-cover px-2 py-1 ring-inset ring-[1.5px] ring-[#5271FF] rounded-full"
                      />
                    </div>
                    <h2 className="text-lg font-medium text-neutral-800 pt-1 mb-0.5 line-clamp-2">
                      {selectedExperience?.name}
                    </h2>

                    {/* Conditionally render address */}
                    <AddressComponent
                      address={placeLocationDetails?.address || ""}
                      isLoading={isLoadingPlaceDetails}
                      experienceName={selectedExperience?.name || ""}
                    />

                    <div className="flex flex-row items-center space-x-2 mt-2">
                      {selectedExperience?.types?.slice(0, 3).map((type) => (
                        <div
                          key={type}
                          className="flex flex-row items-center space-x-2 bg-neutral-100 rounded-full px-3 py-2"
                        >
                          <i
                            className={`far ${getLocationTypeIcon(
                              type
                            )} text-neutral-700 text-xs`}
                          ></i>
                          <p className="text-neutral-700 font-light text-xs leading-relaxed">
                            {type}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex-none flex flex-col items-center justify-start gap-2 ml-4">
                  <button
                    onClick={handleClose}
                    className="text-xs text-neutral-600 hover:text-neutral-800 border border-neutral-200 hover:border-neutral-600 h-8 rounded-full transition-all duration-300 w-16 flex items-center justify-center gap-1"
                  >
                    <i className="fa-solid fa-xmark text-[11px]"></i>
                    Close
                  </button>
                  {isAdmin && (
                    <button
                      onClick={handleAddToDay}
                      className="text-xs text-white bg-indigo-600 hover:bg-indigo-700 h-8 rounded-full transition-all duration-300 w-16 flex items-center justify-center gap-1"
                    >
                      {isAdding ? (
                        <i className="fa-solid fa-spinner fa-spin text-[11px]"></i>
                      ) : (
                        <>
                          <i className="fa-solid fa-plus text-[11px]"></i>
                          <span>Add</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* image carousel */}
              {selectedExperience && selectedExperience?.images?.length > 0 && (
                <div className="relative w-full flex flex-col items-center justify-start py-2">
                  <button
                    onClick={handleLeftClick}
                    className="absolute top-1/2 left-4 -translate-y-1/2 w-7 h-7 rounded-full bg-white bg-opacity-70 hover:bg-opacity-100 shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 z-10"
                    aria-label="Previous image"
                  >
                    <i className="fas fa-sm fa-chevron-left text-gray-800"></i>
                  </button>

                  <div className="w-full max-w-3xl overflow-hidden rounded-lg">
                    <Carousel
                      images={selectedExperience?.images || []}
                      openModal={openGallery}
                    />
                  </div>

                  <button
                    onClick={handleRightClick}
                    className="absolute top-1/2 right-4 -translate-y-1/2 w-7 h-7 rounded-full bg-white bg-opacity-70 hover:bg-opacity-100 shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 z-10"
                    aria-label="Next image"
                  >
                    <i className="fas fa-sm fa-chevron-right text-gray-800"></i>
                  </button>
                </div>
              )}
              <div className="space-y-[2rem] py-4 px-1">
                {/* description */}
                <div className="w-full flex flex-col items-start justify-start">
                  <h3 className="text-[15px] font-semibold mb-2 text-neutral-800">
                    Description
                  </h3>
                  <p className="text-neutral-700 font-light text-[13px] leading-relaxed">
                    {selectedExperience?.description}
                  </p>
                </div>

                {/* price level, time to spend, family friendliness */}
                <div className="grid grid-flow-row auto-rows-auto grid-cols-3 items-center justify-between w-full gap-2">
                  <ExperienceAttribute
                    icon="fa-regular fa-money-bill-wave"
                    name="Price Level"
                    value={selectedExperience?.budget?.length || "N/A"}
                  />
                  <ExperienceAttribute
                    icon="fa-regular fa-stopwatch"
                    name="Time to Spend"
                    value={selectedExperience?.time_to_spend || "N/A"}
                  />
                  <ExperienceAttribute
                    icon="fa-regular fa-family"
                    name="Family Friendliness"
                    value={selectedExperience?.family_friendliness || "N/A"}
                  />
                  <ExperienceAttribute
                    icon="fa-regular fa-calendar-days"
                    name="Season to Visit"
                    value={toTitleCase(
                      selectedExperience?.best_time_to_visit_season || "N/A"
                    )}
                  />
                  <ExperienceAttribute
                    icon="fa-regular fa-sun"
                    name="Time of Day to Visit"
                    value={toTitleCase(
                      selectedExperience?.best_time_to_visit_time_of_day ||
                        "N/A"
                    )}
                  />
                </div>

                {/* locals knowledge */}
                <SectionContainer>
                  <div className="w-full flex flex-col items-start justify-between">
                    <div className="w-full flex flex-col items-start justify-start">
                      <h3 className="text-[15px] font-semibold mb-4 text-neutral-800">
                        Local's Knowledge
                      </h3>
                      <div className="flex flex-row w-full items-start space-x-2 overflow-x-auto no-scrollbar">
                        <div className="flex flex-col items-start space-y-4 w-1/2">
                          {selectedExperience?.pros?.map(
                            (tip: string, index: number) => (
                              <div
                                key={index}
                                className="flex flex-row items-center space-x-3 text-neutral-600"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 512 512"
                                  className="w-4 h-4 flex-shrink-0 fill-green-400 bg-green-200/50 rounded-full"
                                >
                                  <path d="M256 32a224 224 0 1 1 0 448 224 224 0 1 1 0-448zm0 480A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM363.3 203.3c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L224 297.4l-52.7-52.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l64 64c6.2 6.2 16.4 6.2 22.6 0l128-128z" />
                                </svg>
                                <p className="text-neutral-700 font-light text-[13px] leading-relaxed">
                                  {tip}
                                </p>
                              </div>
                            )
                          )}
                        </div>
                        <div className="flex flex-col items-start space-y-4 w-1/2">
                          {selectedExperience?.cons?.map(
                            (tip: string, index: number) => (
                              <div
                                key={index}
                                className="flex flex-row items-center space-x-3 text-neutral-600"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 512 512"
                                  className="w-4 h-4 flex-shrink-0 fill-gray-400 bg-gray-200/50 rounded-full"
                                >
                                  <path d="M256 32a224 224 0 1 1 0 448 224 224 0 1 1 0-448zm0 480A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM180.7 180.7c-6.2 6.2-6.2 16.4 0 22.6L233.4 256l-52.7 52.7c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L256 278.6l52.7 52.7c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L278.6 256l52.7-52.7c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L256 233.4l-52.7-52.7c-6.2-6.2-16.4-6.2-22.6 0z" />
                                </svg>
                                <p className="text-neutral-700 font-light text-[13px] leading-relaxed">
                                  {tip}
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </SectionContainer>
                {/* history & trivia */}
                <SectionContainer className="w-full">
                  <div className="w-full h-full flex flex-col items-start justify-start">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-[15px] font-semibold mb-2 text-neutral-800">
                        History and Trivia
                      </h3>
                    </div>
                    <p className="text-neutral-600 text-[13.5px] font-[350] leading-relaxed flex-grow">
                      {selectedExperience?.history ||
                        "No historical information available."}
                    </p>
                  </div>
                </SectionContainer>

                <SectionContainer>
                  <div className="w-full flex flex-row items-start justify-between">
                    <div className="w-full flex flex-col items-start justify-start">
                      <h3 className="text-[15px] font-semibold mb-4 text-neutral-800">
                        Where to Find It?
                      </h3>
                      <div className="flex flex-col space-y-3 mb-4">
                        {selectedExperience?.transportation?.map(
                          (transport, index) => (
                            <div
                              key={index}
                              className="flex justify-start items-start space-x-3"
                            >
                              <i
                                className={`shrink-0 w-5 h-5 flex items-center justify-center text-neutral-700 text-sm fa-regular fa-${getTransportIcon(
                                  transport.method.toLowerCase()
                                )}`}
                              ></i>
                              <p className="text-neutral-700 font-light text-[13px] leading-relaxed">
                                {addFullStop(transport.detail)}
                              </p>
                            </div>
                          )
                        )}
                      </div>

                      {/* Conditionally render map */}
                      {isLoadingPlaceDetails ? (
                        <div className="h-[400px] w-full bg-neutral-200 rounded-lg animate-pulse"></div>
                      ) : (
                        <div style={{ width: "100%", height: `${height}px` }}>
                          <MapView
                            latitude={
                              selectedExperience?.latitude ||
                              placeLocationDetails?.latitude ||
                              0
                            }
                            longitude={
                              selectedExperience?.longitude ||
                              placeLocationDetails?.longitude ||
                              0
                            }
                            markerName={selectedExperience?.name}
                            type={selectedExperience?.types[0] || ""}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </SectionContainer>

                {/* tips */}
                <SectionContainer>
                  {(selectedExperience?.tips?.length || 0) > 0 && (
                    <div className="w-full flex flex-row items-start justify-between">
                      <div className="w-full flex flex-col items-start justify-start">
                        <h3 className="text-[15px] font-semibold mb-4 text-neutral-800">
                          Tips & Tricks
                        </h3>
                        <div className="flex flex-col space-y-4 pl-1">
                          {selectedExperience?.tips?.map(
                            (tip: string, index: number) => (
                              <ul
                                key={index}
                                className="flex justify-start items-start space-x-3 relative pl-2 list-disc"
                              >
                                <li className="text-neutral-700 font-light text-[13px] leading-relaxed">
                                  {addFullStop(tip)}
                                </li>
                              </ul>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </SectionContainer>
              </div>
            </div>
          </div>
          <ImageGalleryModal
            images={selectedExperience?.images?.map((image) => image.url) || []}
            isOpen={isGalleryOpen}
            onClose={() => setIsGalleryOpen(false)}
            alt={selectedExperience?.name || ""}
            title={selectedExperience?.name || ""}
          />
        </div>
      </div>
    </motion.div>
  );
};

const LoadingSkeleton = () => {
  return (
    <div className="flex flex-col items-center justify-start relative animate-pulse">
      <div className="w-full h-[calc(100vh-4.5rem)] overflow-hidden rounded-lg mt-2 pb-[5rem]">
        {/* Header section */}
        <div className="flex flex-row justify-between items-center py-3">
          <div className="flex flex-col items-start justify-between">
            {/* Title */}
            <div className="h-6 w-48 bg-neutral-200 rounded-md mb-2"></div>
            {/* Address */}
            <div className="h-4 w-64 bg-neutral-200 rounded-md"></div>
            {/* Type pills */}
            <div className="flex flex-row items-center space-x-2 mt-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-8 w-20 bg-neutral-200 rounded-full"
                ></div>
              ))}
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="h-8 w-16 bg-neutral-200 rounded-full"></div>
            <div className="h-8 w-16 bg-neutral-200 rounded-full"></div>
          </div>
        </div>

        {/* Image carousel skeleton */}
        <div className="w-full h-64 bg-neutral-200 rounded-lg mb-6"></div>

        {/* Content sections */}
        <div className="space-y-[2rem] py-4 px-1">
          {/* Description section */}
          <div className="w-full">
            <div className="h-5 w-24 bg-neutral-200 rounded-md mb-3"></div>
            <div className="space-y-2">
              <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
              <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
              <div className="h-4 w-3/4 bg-neutral-200 rounded-md"></div>
            </div>
          </div>

          {/* Attributes grid */}
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-neutral-200 rounded-lg p-2"></div>
            ))}
          </div>

          {/* Local's Knowledge section */}
          <div className="w-full">
            <div className="h-5 w-32 bg-neutral-200 rounded-md mb-4"></div>
            <div className="flex space-x-2">
              <div className="w-1/2 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="h-4 w-4 bg-neutral-200 rounded-full"></div>
                    <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
                  </div>
                ))}
              </div>
              <div className="w-1/2 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="h-4 w-4 bg-neutral-200 rounded-full"></div>
                    <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Map section */}
          <div className="w-full">
            <div className="h-5 w-32 bg-neutral-200 rounded-md mb-4"></div>
            <div className="h-[400px] w-full bg-neutral-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoreInfoModal;
